/**
 * ChatFAB — Floating Action Button that launches the chat panel.
 * Renders a fixed 💬 button at the bottom-right of any order detail page.
 * Manages the socket lifecycle for the student chat session.
 *
 * Props:
 *   orderId     {string} — the order's displayId (e.g. "ASN-001")
 *   orderTitle  {string} — displayed in chat header (e.g. "Assignment #ASN-001")
 */

import React, { useState, useEffect, useRef } from 'react';
import ChatPanel from './ChatPanel';
import { initSocket } from '../../utils/socket';
import { useChatNotification } from '../../context/ChatNotificationContext';
import './ChatFAB.scss';

const ChatFAB = ({ orderId, orderTitle, autoOpen = false, onOrderUpdate, orderCategory }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpened = useRef(false);
  const { getUnreadCount } = useChatNotification();

  // Auto-open the chat panel on first mount if autoOpen is true
  useEffect(() => {
    if (autoOpen && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setIsOpen(true);
    }
  }, [autoOpen]);

  // Derive unread count from global context (updated via socket events)
  const unreadCount = getUnreadCount(orderId);

  // ── Socket lifecycle ───────────────────────────────────────────────────────
  // Initialize socket when the FAB mounts (if not already connected).
  // The socket persists across open/close of the panel.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      initSocket(token);
    }
    // NOTE: We intentionally do NOT disconnect the socket on unmount.
    // The socket must persist so ChatNotificationContext can continue
    // receiving 'new-notification' events across page navigations.
  }, []);

  // ── Open panel ─────────────────────────────────────────────────────────────
  const handleOpen = () => {
    setIsOpen(true);
    // NOTE: badge is NOT cleared here — it clears only when join-order is emitted
    // inside ChatPanel (via markOrderRead called there).
  };

  // ── Close panel ────────────────────────────────────────────────────────────
  const handleClose = () => {
    setIsOpen(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="dq-chat-fab">
      {/* Animated chat panel */}
      {isOpen && (
        <div className="dq-chat-fab__panel">
          <ChatPanel orderId={orderId} orderTitle={orderTitle} onClose={handleClose} onOrderUpdate={onOrderUpdate} orderCategory={orderCategory} />
        </div>
      )}

      {/* Floating action button */}
      <button
        className={`dq-chat-fab__btn${isOpen ? ' dq-chat-fab__btn--open' : ''}`}
        onClick={isOpen ? handleClose : handleOpen}
        title={isOpen ? 'Close chat' : `Chat about ${orderTitle || orderId}`}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          // Close icon
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          // Chat icon
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}

        {/* Unread badge — only shown when panel is closed and there are unread messages */}
        {!isOpen && unreadCount > 0 && (
          <span className="dq-chat-fab__badge" aria-label={`${unreadCount} unread messages`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatFAB;
